import { Channel, FilterGroup } from '../models/ChannelModel';

const SOURCE_CATEGORY_SHOPPING =
  '(alibaba|amazon|ebay|etsy|mercadolibre|shopify|walmart|google.shopping|igshopping|stripe|shopzilla|shop\\.app|simplycodes)';

const SOURCE_CATEGORY_SEARCH =
  '(google|bing|yahoo|yandex|duckduckgo|baidu|ecosia|ask|aol|naver|daum|sogou|qwant|seznam|rakuten|rambler|msn|lycos|dogpile|biglobe|globo|terra|virgilio|avg)';

const SOURCE_CATEGORY_VIDEO =
  '(youtube|vimeo|twitch|dailymotion|netflix|hulu|iqiyi|wistia|veoh|youku|disneyplus|ted\\.com)';

const SOURCE_CATEGORY_SOCIAL =
  '(facebook|ig|fb|instagram|meta|linkedin|tiktok|twitter|^x$|pinterest|snapchat|reddit)';

const PAID_MEDIUM = '^(.*cp.*|ppc|retargeting|paid.*)$';

// Sources custom de MKT que llegan sin medium (o con uno propio) y que por sí
// solas definen canal. Cada una debe figurar también en
// KNOWN_SOURCES_ALTERNATION para que Otro no las cuente. Sus grupos llevan
// medium=MEDIUM_EMPTY_OR_NOT_KNOWN: si el medium ya define canal (p.ej.
// tiktok / email), manda el medium y la sesión no cuenta dos veces.
const SOURCE_TIKTOK = '^tiktok$';
const SOURCE_INSTAGRAM = '^instagram$';
const SOURCE_ORGANIC_SOCIAL = '^discount$';
const SOURCE_PAID_SEARCH = '^gads$';
const SOURCE_PRESS = '^masquestartups$';
const SOURCE_INFLUENCERS = '^anitamateu20$';
const SOURCE_AI = '^(chatgpt\\.com|perplexity|openai)$';
// Los emails de Klaviyo, mypug y petground traen enlaces mal codificados
// (quoted-printable): "3Dklaviyo=" en vez de "klaviyo". Hasta que se corrijan
// en origen, estos sources y sus mediums aceptan el 3D inicial y el = final.
const SOURCE_EMAIL = '^(3D)?(klaviyo|email)=?$';
const SOURCE_PARTNERSHIPS = '^(3D)?(engage|hotel|mypug|petground)=?$';

// Campañas de TikTok: ES-TTO-* son enlaces orgánicos del perfil; el resto
// (ES-TT-*, o sin campaña) son anuncios.
const TIKTOK_ORGANIC_CAMPAIGN = '^ES-TTO-';
const TIKTOK_PAID_CAMPAIGN = '^(?!ES-TTO-)';

// Campañas de Instagram: NOE20 es el código de una influencer; el resto
// (ES-IGO-*, o sin campaña) es orgánico.
const INSTAGRAM_INFLUENCER_CAMPAIGN = '^NOE20$';
const INSTAGRAM_ORGANIC_CAMPAIGN = '^(?!NOE20$)';

// Mediums que definen un canal por sí solos (single-medium channels) más los
// mediums "paid" que junto con un source paid definen los canales Paid*.
// Excluyendo cualquiera de estos mediums en un grupo de "Otro" elimina:
//   - Display, Organic Search rama 1, Organic Social, Press, Email, WhatsApp,
//     Offline, Influencers, Partnerships, Sales, ATC, Referral, Paid Social.
//   - Paid Shopping / Paid Search rama 2 / Paid Video, ya que los tres exigen
//     medium ∈ PAID_MEDIUM.
const KNOWN_MEDIUMS_ALTERNATION =
  'paid_social$|organic_search$|organic$|organic_social$|social$|social-network$|social-media$|sm$|pr$|(3D)?email=?$|whatsapp$|offline$|qr$|flyer$|evento$|event$|feria$|tvc?$|influencer.*$|(3D)?(partner|partnership|co-?marketing|affiliates?|nl)=?$|sales$|atc$|referral$|display$|banner$|expandable$|interstitial$|cpm$|.*cp.*$|ppc$|paid.*$|retargeting$';

// Sources que por sí solas (con gclid='^$') definen un canal: SEARCH list para
// Organic Search rama 2, facebookads para Paid Social y las SOURCE_* custom.
const KNOWN_SOURCES_ALTERNATION =
  'google$|bing$|yahoo$|yandex$|duckduckgo$|baidu$|ecosia$|ask$|aol$|naver$|daum$|sogou$|qwant$|seznam$|rakuten$|rambler$|msn$|lycos$|dogpile$|biglobe$|globo$|terra$|virgilio$|avg$|facebookads$|tiktok$|instagram$|discount$|gads$|(3D)?(klaviyo|email)=?$|masquestartups$|(3D)?(engage|hotel|mypug|petground)=?$|anitamateu20$|chatgpt\\.com$|perplexity$|openai$';

// Coincide cuando el campo está DEFINIDO y no entra en ningún canal por su sola
// presencia.
const SOURCE_DEFINED_AND_NOT_KNOWN = `^(?!${KNOWN_SOURCES_ALTERNATION}).+$`;
const MEDIUM_DEFINED_AND_NOT_KNOWN = `^(?!${KNOWN_MEDIUMS_ALTERNATION}).+$`;

// Coincide cuando el campo está vacío O su valor no entra en ningún canal por
// su sola presencia. Sirve para añadir, en cada grupo de "Otro", la condición
// transversal "este otro campo no coloca a la sesión en un canal conocido".
const SOURCE_EMPTY_OR_NOT_KNOWN = `^(?!${KNOWN_SOURCES_ALTERNATION}).*$`;
const MEDIUM_EMPTY_OR_NOT_KNOWN = `^(?!${KNOWN_MEDIUMS_ALTERNATION}).*$`;

// Referrer cuyo host es uno de `hosts` o un subdominio suyo. Se ancla el host
// completo para no coincidir con dominios parecidos ni con el nombre dentro
// del path de otra URL.
function hostUrl(hosts: string): string {
  return `https?://([a-z0-9-]+\\.)*(${hosts})(/|$)`;
}

// Referrer de una app Android, que llega como android-app://<paquete>/.
function appUrl(packages: string): string {
  return `android-app://(${packages})(/|$)`;
}

// Etiqueta de host de un webmail: mail.google.com, webmail.sfr.fr,
// mail01.orange.fr…
const WEBMAIL_LABEL = '(web)?mail[0-9]*';

// Buscadores que dejan referrer cuando un usuario clica un resultado orgánico,
// con cualquier dominio de país (google.es, google.com…). Sus subdominios de
// webmail y Gemini no son el buscador: cuentan en Email e IA.
const SEARCH_ENGINE_URL = `(https?://(?!([a-z0-9-]+\\.)*(${WEBMAIL_LABEL}|gemini)\\.)([a-z0-9-]+\\.)*(google|bing|yahoo|duckduckgo|yandex|baidu|ecosia|qwant|naver|startpage|petalsearch|privacywall|swisscows)\\.|${hostUrl(
  'search\\.brave\\.com'
)}|${appUrl('com\\.google\\.android\\.googlequicksearchbox')})`;

// Redes sociales y agregadores de enlaces de perfil, también desde la app de
// LinkedIn.
const SOCIAL_URL = `(${hostUrl(
  'facebook\\.com|instagram\\.com|tiktok\\.com|youtube\\.com|linkedin\\.com|lnkd\\.in|reddit\\.com|threads\\.(com|net)|pinterest\\.[a-z.]+|linktr\\.ee|lnk\\.bio|discord\\.com|t\\.me|telegram\\.org'
)}|${appUrl('com\\.linkedin\\.android')})`;

// Clics de email sin UTMs: el tracking de Klaviyo, Outlook, la app de Gmail,
// cualquier webmail y los redirectores de seguridad del correo (deref de GMX,
// safelinks de Outlook).
const EMAIL_URL = `(${hostUrl(
  'klclick\\.com|kmail-lists\\.com|outlook\\.(live|office|office365)\\.com'
)}|https?://([a-z0-9-]+\\.)*(${WEBMAIL_LABEL}|[a-z0-9-]*deref[a-z0-9-]*|safelinks)\\.|${appUrl(
  'com\\.google\\.android\\.gm'
)})`;

// Enlaces abiertos desde WhatsApp.
const WHATSAPP_URL = hostUrl('l\\.wl\\.co');

// Asistentes de IA que enlazan sin UTMs (ChatGPT solo añade utm_source a
// veces).
const AI_URL = hostUrl(
  'chatgpt\\.com|perplexity\\.ai|claude\\.ai|gemini\\.google\\.com|copilot\\.com|qwen\\.ai|minimax\\.io'
);

// Medios españoles.
const PRESS_URL = hostUrl(
  'elperiodico\\.com|elconfidencial\\.com|cadenaser\\.com|lavanguardia\\.com|telecinco\\.es|mediasetinfinity\\.es|hosteltur\\.com'
);

// Mutua y blogs de mascotas con los que colaboramos.
const PARTNERSHIPS_URL = hostUrl(
  'mutua\\.es|doglifemagazine\\.es|dogsplanet\\.com|patitasco\\.com'
);

// Sitios externos que enlazan sin UTMs y MKT cuenta como Referral.
const REFERRAL_SITE_URL = hostUrl(
  [
    // Afiliados (bbux.me es de TradeTracker) y webs de arbitraje, incluidas
    // las del pico de septiembre que se están revisando con TradeTracker.
    'bbux\\.me',
    'tradetracker\\.com',
    'performancehorizon\\.com',
    'clicknshopnow\\.com',
    'simplemusehub\\.com',
    'wandersglam\\.com',
    'dealtrackelite\\.com',
    'similarsearch\\.net',
    'seetrueworth\\.net',
    'inspiredtoday\\.net',
    'strategicimprovementplan\\.net',
    'top-best\\.com',
    'dailyslate\\.site',
    'becovi\\.net',
    'fizzdaily\\.com',
    'readlatertab\\.com',
    'ginksearch\\.com',
    'realtimesearchresults\\.com',
    'yellow-search\\.org',
    'searchbip\\.com',
    'alohafind\\.com',
    'savvydealcentral\\.com',
    'trendupdeal\\.com',
    'choicewhisper\\.com',
    'ai2b0\\.com',
    'zasobygwp\\.pl',
    // Chollos, reseñas y otros sitios que nos enlazan.
    'chollo\\.es',
    'rebajas\\.guru',
    'trustpilot\\.com',
    'msn\\.com',
    'luccalaloca\\.es',
  ].join('|')
);

// Referrers que no son un origen: nuestros propios dominios (landings de go.,
// store., crm.…) y el retorno de las pasarelas de pago. La captura web debería
// vaciarlos, como ya hace con los referrers internos; mientras se le escapen,
// cuentan como Direct.
const IGNORED_REFERRER_URL = hostUrl(
  'foodforjoe\\.(es|com)|redsys\\.es|revolut\\.com|abanca\\.com|marqeta\\.com|cardinalcommerce\\.com|multisafepay\\.com|mbank\\.pl|shop\\.app'
);

// Referrers que coloca algún canal. Una lista nueva tiene que entrar aquí para
// que Otro no la cuente.
const KNOWN_REFERRER_URLS = [
  SEARCH_ENGINE_URL,
  SOCIAL_URL,
  EMAIL_URL,
  WHATSAPP_URL,
  AI_URL,
  PRESS_URL,
  PARTNERSHIPS_URL,
  REFERRAL_SITE_URL,
  IGNORED_REFERRER_URL,
].join('|');

const SEARCH_ENGINE_REFERRER = `^${SEARCH_ENGINE_URL}`;

// gclid con referrer vacío o que no es un buscador va a Cross-network (no es
// Paid Search rama 1); sin UTMs ni gclid, un referrer que ningún canal
// reconoce va a Otro.
const REFERRER_NOT_SEARCH_ENGINE = `^(?!${SEARCH_ENGINE_URL}).*$`;
const REFERRER_DEFINED_AND_NOT_KNOWN = `^(?!${KNOWN_REFERRER_URLS}).+$`;

// Los grupos por referrer exigen los cinco UTMs vacíos: así los UTMs mandan
// sobre el referrer y la sesión no cuenta también en Otro (grupos 3–5) ni en
// Cross-network.
const NO_UTMS: FilterGroup = {
  source: '^$',
  medium: '^$',
  campaign: '^$',
  term: '^$',
  content: '^$',
};

// gclid='^$' (no es Paid Search rama 1) se añade a todos los canales excepto
// al propio Paid Search, para evitar el solapamiento con Paid Search rama 1
// cuando una sesión llega autotagged por Google Ads.
const NO_GCLID = '^$';

// Sesión sin UTMs ni gclid que llega desde una de las URLs de `url`.
function referrerGroup(url: string): FilterGroup {
  return { ...NO_UTMS, referrer: `^${url}`, gclid: NO_GCLID };
}

export const CHANNELS: Channel[] = [
  // ── GA4 spec ──────────────────────────────────────────────────────────────

  // Cross-network: campaign = "cross-network" es la convención GA4 para UTMs
  // manuales. Además, clics de Google Ads (gclid) que no llegan desde un
  // buscador: Performance Max / Demand Gen en YouTube, Display, Discover o
  // Gmail. Incluye los de buscador cuyo referrer se pierde por el camino.
  {
    name: 'Cross-network',
    groups: [
      { campaign: '^cross-network$', gclid: NO_GCLID },
      { gclid: '.+', referrer: REFERRER_NOT_SEARCH_ENGINE },
    ],
  },

  // Paid Shopping: SOURCE_CATEGORY_SHOPPING (lista oficial GA4) + medium paid (patrón oficial GA4).
  {
    name: 'Paid Shopping',
    groups: [
      {
        source: SOURCE_CATEGORY_SHOPPING,
        medium: PAID_MEDIUM,
        gclid: NO_GCLID,
      },
    ],
  },

  // Paid Search: cualquier sesión con gclid (Google Ads autotagea solo en clics
  // paid). Fallback para UTMs etiquetadas a mano sin gclid (p.ej. Bing manual).
  {
    name: 'Paid Search',
    groups: [
      { gclid: '.+', referrer: SEARCH_ENGINE_REFERRER },
      { source: SOURCE_CATEGORY_SEARCH, medium: PAID_MEDIUM, gclid: NO_GCLID },
      {
        source: SOURCE_PAID_SEARCH,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
    ],
  },

  // Paid Video: SOURCE_CATEGORY_VIDEO (lista oficial GA4) + medium paid (patrón oficial GA4).
  {
    name: 'Paid Video',
    groups: [
      { source: SOURCE_CATEGORY_VIDEO, medium: PAID_MEDIUM, gclid: NO_GCLID },
    ],
  },

  // Display: medio = display/banner es la convención estándar para UTMs manuales.
  {
    name: 'Display',
    groups: [
      {
        medium: '^(display|banner|expandable|interstitial|cpm)$',
        gclid: NO_GCLID,
      },
    ],
  },

  // ── Canales custom (definidos por el equipo de MKT) ────────────────────────

  {
    name: 'Paid Social',
    groups: [
      {
        source: SOURCE_CATEGORY_SOCIAL,
        medium: '(paid_social|cpc|ppc|paid)',
        gclid: NO_GCLID,
      },
      { source: '^facebookads$', gclid: NO_GCLID },
      {
        source: SOURCE_TIKTOK,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        campaign: TIKTOK_PAID_CAMPAIGN,
        gclid: NO_GCLID,
      },
    ],
  },

  // Organic Search: tres formas legítimas de detectarlo, OR'd.
  //  1) UTM manual explícito.
  //  2) UTM con source = buscador y SIN gclid (descarta Paid Search).
  //  3) Sin UTMs ni gclid pero referrer es un buscador (auto-detección
  //     cuando la página de aterrizaje no tiene UTMs).
  {
    name: 'Organic Search',
    groups: [
      { medium: '^organic$', gclid: NO_GCLID },
      { source: SOURCE_CATEGORY_SEARCH, gclid: NO_GCLID },
      {
        referrer: SEARCH_ENGINE_REFERRER,
        source: '^$',
        medium: '^$',
        gclid: NO_GCLID,
      },
    ],
  },

  // IA: asistentes de IA, por utm_source o, sin UTMs, por referrer.
  {
    name: 'IA',
    groups: [
      { source: SOURCE_AI, medium: MEDIUM_EMPTY_OR_NOT_KNOWN, gclid: NO_GCLID },
      referrerGroup(AI_URL),
    ],
  },

  {
    name: 'Organic Social',
    groups: [
      {
        medium: '^(organic_social|social|social-network|social-media|sm)$',
        gclid: NO_GCLID,
      },
      {
        source: SOURCE_TIKTOK,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        campaign: TIKTOK_ORGANIC_CAMPAIGN,
        gclid: NO_GCLID,
      },
      {
        source: SOURCE_INSTAGRAM,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        campaign: INSTAGRAM_ORGANIC_CAMPAIGN,
        gclid: NO_GCLID,
      },
      // Promos publicadas en stories / ManyChat de Instagram.
      {
        source: SOURCE_ORGANIC_SOCIAL,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      referrerGroup(SOCIAL_URL),
    ],
  },

  {
    name: 'Press',
    groups: [
      { medium: '^pr$', gclid: NO_GCLID },
      // Podcast "Más que startups".
      {
        source: SOURCE_PRESS,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      referrerGroup(PRESS_URL),
    ],
  },

  {
    name: 'Email',
    groups: [
      { medium: '^(3D)?email=?$', gclid: NO_GCLID },
      {
        source: SOURCE_EMAIL,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      referrerGroup(EMAIL_URL),
    ],
  },

  {
    name: 'WhatsApp',
    groups: [
      { medium: '^whatsapp$', gclid: NO_GCLID },
      referrerGroup(WHATSAPP_URL),
    ],
  },

  // Offline: tv / tvc son anuncios en televisión.
  {
    name: 'Offline',
    groups: [
      {
        medium: '^(offline|qr|flyer|evento|event|feria|tvc?)$',
        gclid: NO_GCLID,
      },
    ],
  },

  {
    name: 'Influencers',
    groups: [
      { medium: '^influencer.*$', gclid: NO_GCLID },
      {
        source: SOURCE_INSTAGRAM,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        campaign: INSTAGRAM_INFLUENCER_CAMPAIGN,
        gclid: NO_GCLID,
      },
      {
        source: SOURCE_INFLUENCERS,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
    ],
  },

  // Partnerships: nl es la newsletter de MutuaPets y engage sus posts; hotel
  // los hoteles con los que colaboramos (p.ej. Meliá vía Patasbox).
  {
    name: 'Partnerships',
    groups: [
      {
        medium: '^(3D)?(partner|partnership|co-?marketing|affiliates?|nl)=?$',
        gclid: NO_GCLID,
      },
      {
        source: SOURCE_PARTNERSHIPS,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      referrerGroup(PARTNERSHIPS_URL),
    ],
  },

  {
    name: 'Sales',
    groups: [{ medium: '^sales$', gclid: NO_GCLID }],
  },

  {
    name: 'ATC',
    groups: [{ medium: '^atc$', gclid: NO_GCLID }],
  },

  {
    name: 'Referral',
    groups: [
      { medium: '^referral$', gclid: NO_GCLID },
      referrerGroup(REFERRAL_SITE_URL),
    ],
  },

  // Direct: sin UTMs, sin gclid y sin un referrer que sea un origen: vacío
  // (el usuario tecleó la URL o llegó desde un cliente de email/bookmark) o
  // uno de los que la captura web debería haber vaciado.
  {
    name: 'Direct',
    groups: [
      { ...NO_UTMS, referrer: '^$', gclid: NO_GCLID },
      referrerGroup(IGNORED_REFERRER_URL),
    ],
  },

  // Otro: cajón de sastre para lo que no encaja en ningún otro canal. Por
  // UTMs, cinco grupos OR'd (uno por UTM focal — el mínimo posible en el
  // modelo OR-de-AND, ya que el "alguno definido" es una disyunción que no
  // cabe dentro de un grupo AND). Sin UTMs ni gclid, uno más: referrer
  // externo que ningún canal reconoce.
  //
  // Cada grupo añade en los OTROS campos las condiciones que descartan los
  // canales single-dim (Paid Search rama 1, Display, Organic Search rama 1/2,
  // Organic Social, Press/Email/.../Referral) y los multi-dim que comparten
  // medium∈PAID (Paid Shopping/Search rama 2/Video/Social).
  //
  // El solape con Cross-network se acepta a propósito (ya documentado): no
  // exigimos `campaign != cross-network`, así una sesión cross-network que
  // tenga otros UTMs raros cuenta también en Otro. Esto ahorra una regex con
  // negative-lookahead por grupo.
  //
  // Edge case admitido: sesiones con medium∈paid pero source NO en la lista
  // paid_source correspondiente (p.ej. medium=cpc, source=blog_raro)
  // quedan sin contar en ningún canal — UTMs mal etiquetados a mano.
  {
    name: 'Otro',
    groups: [
      // 1. source definido y no en lista canal-conocido.
      {
        source: SOURCE_DEFINED_AND_NOT_KNOWN,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      // 2. medium definido y no en lista canal-conocido.
      {
        source: SOURCE_EMPTY_OR_NOT_KNOWN,
        medium: MEDIUM_DEFINED_AND_NOT_KNOWN,
        gclid: NO_GCLID,
      },
      // 3. campaign definida (cualquier valor, incluido cross-network).
      {
        source: SOURCE_EMPTY_OR_NOT_KNOWN,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        campaign: '.+',
        gclid: NO_GCLID,
      },
      // 4. term definido (ningún canal lo filtra).
      {
        source: SOURCE_EMPTY_OR_NOT_KNOWN,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        term: '.+',
        gclid: NO_GCLID,
      },
      // 5. content definido (ningún canal lo filtra).
      {
        source: SOURCE_EMPTY_OR_NOT_KNOWN,
        medium: MEDIUM_EMPTY_OR_NOT_KNOWN,
        content: '.+',
        gclid: NO_GCLID,
      },
      // 6. Sin UTMs ni gclid, con un referrer que ningún canal reconoce.
      {
        ...NO_UTMS,
        referrer: REFERRER_DEFINED_AND_NOT_KNOWN,
        gclid: NO_GCLID,
      },
    ],
  },
];
